import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import { autoMobileInvoiceService } from '../../../../services/autoMobileInvoiceService';
import { useCurrencyFormat } from '../../../../hooks/useCurrency';
import { ArrowLeft, FileText, Send, CreditCard, Ban, X } from 'lucide-react';
import '../AutoMobilePage.css';

const InvoiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fmt = useCurrencyFormat();

  const [invoice, setInvoice] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showPayModal, setShowPayModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [amountPaid, setAmountPaid] = useState(0);

  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await autoMobileInvoiceService.getById(parseInt(id));
      const d = (res.data as any)?.data ?? res.data;
      setInvoice(d);
      setAmountPaid(d?.totalAmount ?? 0);
    } catch {
      setError(t('automobile.invoice.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => { load(); }, [load]);

  const handleIssue = async () => {
    if (!invoice) return;
    setUpdating(true);
    try {
      await autoMobileInvoiceService.issue(invoice.id);
      await load();
    } catch {
      setError(t('automobile.invoice.failedToIssue'));
    } finally {
      setUpdating(false);
    }
  };

  const confirmPay = async () => {
    if (!invoice) return;
    setUpdating(true);
    try {
      await autoMobileInvoiceService.pay(invoice.id, { amountPaid, paymentMethod });
      setShowPayModal(false);
      await load();
    } catch {
      setError(t('automobile.invoice.failedToPay'));
    } finally {
      setUpdating(false);
    }
  };

  const confirmVoid = async () => {
    if (!invoice || !voidReason.trim()) return;
    setUpdating(true);
    try {
      await autoMobileInvoiceService.void(invoice.id, voidReason.trim());
      setShowVoidModal(false);
      await load();
    } catch {
      setError(t('automobile.invoice.failedToVoid'));
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="ims-page auto-page" style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <div className="spinner-border" role="status"><span className="visually-hidden">{t('common.loading')}</span></div>
        </div>
      </Layout>
    );
  }

  if (!invoice) {
    return (
      <Layout>
        <div className="ims-page auto-page">
          <div className="ims-alert ims-alert-error">{error ?? t('automobile.invoice.notFound')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="ims-detail-page auto-detail-page">
        <div className="ims-hero auto-hero">
          <div className="ims-hero-nav">
            <button className="ims-hero-back" onClick={() => navigate('/automobile/invoices')}>
              <ArrowLeft size={14} />
              {t('automobile.invoice.title')}
            </button>
          </div>
          <div className="ims-hero-content">
            <div>
              <h1 className="ims-hero-number">{invoice.invoiceNumber}</h1>
              <p className="ims-hero-supplier">
                {invoice.vehicleRegistrationNumber} · {invoice.vehicleMakeModel} · {invoice.customerName}
              </p>
              <div className="ims-hero-badges">
                <span className={`ims-hero-badge auto-job-${invoice.jobType}`}>
                  {t(`automobile.invoice.jobTypes.${invoice.jobType}`, invoice.jobType)}
                </span>
                <span className={`ims-hero-badge auto-badge-${invoice.status}`}>
                  {t(`automobile.invoice.statusLabels.${invoice.status}`, invoice.status)}
                </span>
              </div>
            </div>
            <div className="ims-hero-meta">
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {invoice.status === 'Draft' && (
                  <>
                    <button className="ims-btn ims-btn-secondary" onClick={() => navigate(`/automobile/invoices/${invoice.id}/edit`)} disabled={updating}>
                      {t('common.edit')}
                    </button>
                    <button className="ims-btn ims-btn-primary" onClick={handleIssue} disabled={updating}>
                      <Send size={14} style={{ marginInlineEnd: 4 }} /> {t('automobile.invoice.issue')}
                    </button>
                  </>
                )}
                {invoice.status === 'Issued' && (
                  <button className="ims-btn ims-btn-primary" onClick={() => setShowPayModal(true)} disabled={updating}>
                    <CreditCard size={14} style={{ marginInlineEnd: 4 }} /> {t('automobile.invoice.recordPayment')}
                  </button>
                )}
                {!['Voided', 'Paid'].includes(invoice.status) && (
                  <button className="btn-delete" onClick={() => setShowVoidModal(true)} disabled={updating}>
                    <Ban size={14} style={{ marginInlineEnd: 4 }} /> {t('automobile.invoice.void')}
                  </button>
                )}
              </div>
              <span className="ims-hero-date">
                {invoice.createdAt ? new Date(invoice.createdAt).toLocaleDateString() : ''}
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginBlock: '1rem' }}>
            {error}
            <button className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <div className="ims-detail-body">
          <div className="ims-detail-main">
            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">
                  <FileText size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                  {t('automobile.invoice.lines')}
                </span>
              </div>
              <div className="ims-card-body" style={{ padding: 0 }}>
                <table className="auto-lines-table">
                  <thead>
                    <tr>
                      <th>{t('automobile.invoice.description')}</th>
                      <th style={{ width: '90px' }}>{t('automobile.invoice.lineType')}</th>
                      <th className="num" style={{ width: '80px' }}>{t('automobile.invoice.qty')}</th>
                      <th className="num" style={{ width: '110px' }}>{t('automobile.invoice.unitPrice')}</th>
                      <th className="num" style={{ width: '110px' }}>{t('automobile.invoice.lineTotal')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(invoice.lines ?? []).map((l: any) => (
                      <tr key={l.id}>
                        <td style={{ fontWeight: 500 }}>{l.description}</td>
                        <td>{t(`automobile.invoice.lineTypes.${l.lineType}`, l.lineType)}</td>
                        <td className="num">{l.quantity}</td>
                        <td className="num">{fmt(l.unitPrice)}</td>
                        <td className="num" style={{ fontWeight: 600 }}>{fmt(l.lineTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="auto-lines-totals">
                  <div className="auto-lines-totals-row"><span>{t('automobile.invoice.subTotal')}</span><span>{fmt(invoice.subTotal)}</span></div>
                  <div className="auto-lines-totals-row"><span>{t('automobile.invoice.tax')}</span><span>{fmt(invoice.taxAmount)}</span></div>
                  <div className="auto-lines-totals-row"><span>{t('automobile.invoice.discount')}</span><span>-{fmt(invoice.discountAmount)}</span></div>
                  <div className="auto-lines-totals-row grand"><span>{t('automobile.invoice.total')}</span><span>{fmt(invoice.totalAmount)}</span></div>
                </div>
              </div>
            </div>
          </div>

          <div className="ims-detail-sidebar">
            {invoice.status === 'Voided' && (
              <div className="ims-card" style={{ marginBottom: '1rem' }}>
                <div className="ims-card-header">
                  <span className="ims-card-title"><Ban size={15} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />{t('automobile.invoice.voided')}</span>
                </div>
                <div className="ims-card-body">
                  <div style={{ fontSize: 13 }}>
                    <span style={{ color: 'var(--ims-text-sub)' }}>{t('automobile.invoice.reason')}: </span>
                    <span style={{ fontWeight: 600 }}>{invoice.voidReason || '—'}</span>
                  </div>
                </div>
              </div>
            )}

            {invoice.status === 'Paid' && (
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title"><CreditCard size={15} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />{t('automobile.invoice.payment')}</span>
                  <span className="ims-badge ims-badge-approved">{t('automobile.invoice.statusLabels.Paid')}</span>
                </div>
                <div className="ims-card-body">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--ims-text-sub)' }}>{t('automobile.invoice.method')}</span>
                      <span style={{ fontWeight: 600 }}>{invoice.paymentMethod || '—'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--ims-text-sub)' }}>{t('automobile.invoice.paid')}</span>
                      <span>{fmt(invoice.amountPaid)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="ims-card" style={{ marginTop: invoice.status === 'Paid' || invoice.status === 'Voided' ? '1rem' : 0 }}>
              <div className="ims-card-header"><span className="ims-card-title">{t('automobile.vehicle.title')}</span></div>
              <div className="ims-card-body">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--ims-text-sub)' }}>{t('automobile.vehicle.registrationNumber')}</span>
                    <span style={{ fontWeight: 600 }}>{invoice.vehicleRegistrationNumber}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--ims-text-sub)' }}>{t('automobile.invoice.jobType')}</span>
                    <span style={{ fontWeight: 600 }}>{t(`automobile.invoice.jobTypes.${invoice.jobType}`, invoice.jobType)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--ims-text-sub)' }}>{t('automobile.invoice.mileageAtService')}</span>
                    <span>{invoice.mileageAtService?.toLocaleString?.() ?? invoice.mileageAtService}</span>
                  </div>
                </div>
                {invoice.notes && <p style={{ marginTop: '1rem', fontSize: 12, color: 'var(--ims-text-sub)' }}>{invoice.notes}</p>}
              </div>
            </div>
          </div>
        </div>

        {showPayModal && (
          <div className="auto-void-overlay" onClick={() => !updating && setShowPayModal(false)}>
            <div className="auto-void-modal" onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <CreditCard size={17} />
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, flex: 1 }}>{t('automobile.invoice.recordPayment')}</h3>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setShowPayModal(false)} disabled={updating}>
                  <X size={16} />
                </button>
              </div>
              <div className="mi-field">
                <label className="mi-label">{t('automobile.invoice.amountPaid')}</label>
                <input type="number" className="mi-input" value={amountPaid} min={0} step={0.01}
                  onChange={e => setAmountPaid(Number(e.target.value))} />
              </div>
              <div className="mi-field">
                <label className="mi-label">{t('automobile.invoice.method')}</label>
                <select className="mi-input" value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                  <option value="Cash">{t('automobile.invoice.paymentMethods.Cash')}</option>
                  <option value="Card">{t('automobile.invoice.paymentMethods.Card')}</option>
                  <option value="BankTransfer">{t('automobile.invoice.paymentMethods.BankTransfer')}</option>
                </select>
              </div>
              <div className="auto-void-actions">
                <button className="ims-btn ims-btn-secondary" onClick={() => setShowPayModal(false)} disabled={updating}>
                  {t('common.cancel')}
                </button>
                <button className="ims-btn ims-btn-primary" onClick={confirmPay} disabled={updating}>
                  {updating ? t('automobile.invoice.paying') : t('automobile.invoice.confirmPayment')}
                </button>
              </div>
            </div>
          </div>
        )}

        {showVoidModal && (
          <div className="auto-void-overlay" onClick={() => !updating && setShowVoidModal(false)}>
            <div className="auto-void-modal" onClick={e => e.stopPropagation()}>
              <h3 className="auto-void-title">
                <Ban size={17} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                {t('automobile.invoice.voidModal.title', { number: invoice.invoiceNumber })}
              </h3>
              <p className="auto-void-hint">{t('automobile.invoice.voidModal.hint')}</p>
              <label className="auto-void-label">{t('automobile.invoice.voidModal.reasonLabel')}</label>
              <textarea className="auto-void-textarea" rows={3} maxLength={500}
                placeholder={t('automobile.invoice.voidModal.placeholder')}
                value={voidReason} onChange={e => setVoidReason(e.target.value)} autoFocus />
              <div className="auto-void-actions">
                <button className="ims-btn ims-btn-secondary" onClick={() => setShowVoidModal(false)} disabled={updating}>
                  {t('automobile.invoice.voidModal.keep')}
                </button>
                <button className="btn-delete" onClick={confirmVoid} disabled={updating || !voidReason.trim()}>
                  {updating ? t('automobile.invoice.voidModal.voiding') : t('automobile.invoice.voidModal.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default InvoiceDetailPage;
